import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/auth";
import { fmtDateTime, STATUS_META } from "@/components/portal/parts";

const EVENT_LABEL = {
  reservation_confirmed: "Reservation confirmed", chauffeur_assigned: "Chauffeur assigned",
  chauffeur_en_route: "Chauffeur en route", chauffeur_arrived: "Chauffeur arrived",
  passenger_picked_up: "Passenger picked up", in_progress: "Trip in progress",
  reservation_completed: "Reservation completed", reservation_canceled: "Reservation canceled",
  schedule_changed: "Schedule change",
};

export default function NotificationBell({ onOpenReservation }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const ref = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/notifications?limit=30"); setData(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const openItem = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.notification_id}/read`); }
    setOpen(false);
    load();
    if (onOpenReservation && n.reservation_id) onOpenReservation(n.reservation_id);
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={() => setOpen((o) => !o)} className="relative rounded-full border border-gold/30 p-2 text-ivory/80 hover:bg-gold/10">
        <Bell className="h-4 w-4" />
        {data.unread_count > 0 && (
          <span data-testid="notification-badge" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-obsidian-900">{data.unread_count}</span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel" className="absolute right-0 z-30 mt-2 w-80 overflow-hidden rounded-2xl border border-gold/20 bg-obsidian-800 shadow-2xl">
          <div className="flex items-center justify-between border-b border-gold/10 px-4 py-3">
            <span className="font-mono text-[11px] uppercase tracking-widest text-gold">Notifications</span>
            {data.unread_count > 0 && <button data-testid="mark-all-read" onClick={markAll} className="inline-flex items-center gap-1 text-xs text-ivory/60 hover:text-gold"><Check className="h-3 w-3" /> Mark all read</button>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.notifications.length === 0 && <p className="px-4 py-8 text-center text-sm text-ivory/45" data-testid="no-notifications">No notifications yet.</p>}
            {data.notifications.map((n) => {
              const color = (STATUS_META[n.event_type] || {}).color || "#D4AF37";
              return (
                <button key={n.notification_id} onClick={() => openItem(n)} data-testid={`notification-item-${n.confirmation_number}-${n.event_type}`} className={`flex w-full items-start gap-3 border-b border-gold/5 px-4 py-3 text-left transition hover:bg-gold/5 ${n.read ? "opacity-60" : ""}`}>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-gold" />}
                  <div className={n.read ? "pl-5" : ""}>
                    <p className="text-sm text-ivory">{EVENT_LABEL[n.event_type] || n.title}</p>
                    <p className="text-xs text-gold/80">{n.confirmation_number}</p>
                    <p className="text-[11px] text-ivory/40">{fmtDateTime(n.created_at)}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
